import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import * as React from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { RobotoTooltip } from "@/shared/components";
import { AlertDialog } from "@/shared/components/AlertDialog";
import {
  ResourceView,
  useResourceView,
  type ActiveFiltersState,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { assignTagColor } from "@/shared/components/TagList/helpers";
import {
  canEditSkill,
  consumeSkillDraft,
  SkillAccessibility,
  SkillDraft,
  SkillListScope,
  SkillSummary,
} from "@/shared/domain/skills";
import {
  useDeleteSkill,
  useDeleteVersion,
  useSetSubscription,
  useSkills,
  useSkillVersions,
  useSubscribeSkill,
  useUnsubscribeSkill,
  useUpdateSkillMetadata,
} from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  DataTable,
  type ColDef,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";

import { skillMatchesFilters } from "./filterSkills";
import { SkillEditor } from "./SkillEditor";
import { SkillInspector } from "./SkillInspector";
import { SkillMetadataEditor } from "./SkillMetadataEditor";
import styles from "./Skills.module.css";
import { TagsEditor } from "./TagsEditor";

interface EditorTarget {
  summary: SkillSummary;
  version: number | null;
}

/** Display label for each accessibility tier — used by the visibility chip. */
const ACCESSIBILITY_LABEL: Record<SkillAccessibility, string> = {
  private: "Private",
  org: "Org",
  "org-editable": "Org-editable",
};

/**
 * MUI Chip color per accessibility tier. `private` is the neutral grey;
 * `org` is the primary blue; `org-editable` gets its own green so the
 * "anyone can edit this" tier reads as distinct at a glance.
 */
const ACCESSIBILITY_CHIP_COLOR: Record<
  SkillAccessibility,
  "default" | "primary" | "success"
> = {
  private: "default",
  org: "primary",
  "org-editable": "success",
};

/**
 * Author-facing menu copy for each tier, shown in the visibility dropdown.
 * Tiers are ordered private → org → org-editable; the dropdown lets the
 * author jump straight to any tier (no cycle), so downgrading `org-editable`
 * to plain `org` never has to pass through `private` — which would prune
 * every non-author subscription.
 */
const ACCESSIBILITY_MENU_COPY: Record<SkillAccessibility, string> = {
  private: "Private (only you)",
  org: "Org (everyone in your org can use it)",
  "org-editable": "Org-editable (everyone in your org can use and edit it)",
};

const ACCESSIBILITY_ORDER: SkillAccessibility[] = [
  "private",
  "org",
  "org-editable",
];

/** The colored visibility chip — shown standalone for non-authors and as the
 *  collapsed value of the author's accessibility dropdown. */
function VisibilityChip({
  accessibility,
  pending,
}: {
  accessibility: SkillAccessibility;
  pending?: boolean;
}) {
  return (
    <Chip
      size="small"
      label={ACCESSIBILITY_LABEL[accessibility]}
      color={ACCESSIBILITY_CHIP_COLOR[accessibility]}
      icon={
        pending ? (
          <CircularProgress
            size={12}
            className={styles.visibilityChipSpinner}
          />
        ) : undefined
      }
    />
  );
}

interface VisibilitySelectCellProps {
  summary: SkillSummary;
  accessibility: SkillAccessibility;
  isPending: boolean;
  onChange: (summary: SkillSummary, next: SkillAccessibility) => void;
}

/**
 * Author-only visibility dropdown. The tooltip is controlled rather than
 * left in MUI's default hover mode so it can be force-closed while the
 * Select menu is open — an uncontrolled tooltip stays mounted on top of the
 * open dropdown list and obscures the tier options.
 */
function VisibilitySelectCell({
  summary,
  accessibility,
  isPending,
  onChange,
}: VisibilitySelectCellProps) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  return (
    <RobotoTooltip
      title="Change who can see and edit this skill"
      open={hovering && !menuOpen}
      onOpen={() => setHovering(true)}
      onClose={() => setHovering(false)}
    >
      <Select
        size="small"
        variant="standard"
        disableUnderline
        value={accessibility}
        disabled={isPending}
        className={styles.visibilitySelect}
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        onChange={(e) =>
          onChange(summary, e.target.value as SkillAccessibility)
        }
        renderValue={(value) => (
          <VisibilityChip
            accessibility={value as SkillAccessibility}
            pending={isPending}
          />
        )}
      >
        {ACCESSIBILITY_ORDER.map((tier) => (
          <MenuItem key={tier} value={tier}>
            {ACCESSIBILITY_MENU_COPY[tier]}
          </MenuItem>
        ))}
      </Select>
    </RobotoTooltip>
  );
}

interface VersionDeleteTarget {
  summary: SkillSummary;
  version: number;
  isLastVersion: boolean;
}

export function Skills() {
  const { currentOrganization, currentUser } = useAuth();
  const orgId = currentOrganization?.org_id;
  const userId = currentUser?.user_id ?? "";

  // Deep-link seeds (e.g. the "Edit skill" link from the agent template's
  // skill overlay): land on the right tab, prefill a name filter so only the
  // target skill shows, and expand its versions panel where the body editor
  // lives. Captured once at mount — the filter and expansion state are
  // mount-snapshotted downstream, so a later URL edit must not re-seed them.
  const [searchParams] = useSearchParams();
  const [linkSeed] = React.useState<{
    tab: SkillListScope;
    name: string | null;
    expandSkillId: string | null;
  }>(() => ({
    tab: searchParams.get("tab") === "org" ? "org" : "personal",
    name: searchParams.get("name")?.trim() || null,
    expandSkillId: searchParams.get("skillId") || null,
  }));
  // A fresh object each render is harmless here: the consumers snapshot it at
  // mount, so neither seed needs memoizing.
  const initialActiveFilters: ActiveFiltersState | undefined = linkSeed.name
    ? {
        type: "structured",
        filters: [
          {
            type: "string",
            field: "name",
            comparator: "EQUALS",
            values: [linkSeed.name],
          },
        ],
        matchMode: "AND",
      }
    : undefined;
  const initialExpandedIds = linkSeed.expandSkillId
    ? new Set([linkSeed.expandSkillId])
    : undefined;

  const [tab, setTab] = React.useState<SkillListScope>(linkSeed.tab);
  const skillsQuery = useSkills(orgId, tab);
  const deleteSkill = useDeleteSkill(orgId);
  const updateMetadata = useUpdateSkillMetadata(orgId);
  const subscribeSkill = useSubscribeSkill(orgId);
  const unsubscribeSkill = useUnsubscribeSkill(orgId);
  const setSubscription = useSetSubscription(orgId);

  // Pending create-skill draft handed over from the chat tool's
  // `client_create_skill` approval, which stashes a draft and navigates here.
  // When present we open the create editor seeded with it. Cleared by a manual
  // "Create Skill" click so that path always starts from the empty form.
  const [createSeed, setCreateSeed] = React.useState<SkillDraft | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingTarget, setEditingTarget] = React.useState<EditorTarget | null>(
    null,
  );

  // Consume the draft on mount AND on every subsequent navigation to this
  // route, keyed on `location.key`. The chat tool navigates here after
  // stashing, but when the Skills page is already mounted (e.g. the chat panel
  // is open over it) that navigation re-keys the location without remounting —
  // a mount-only read would miss the draft and the editor would never open.
  // `consumeSkillDraft` is StrictMode-safe and clears the slot, so a navigation
  // with no pending draft (a filter change, a normal visit) reads null and is a
  // no-op.
  const location = useLocation();
  React.useEffect(() => {
    const draft = consumeSkillDraft();
    if (draft === null) {
      return;
    }
    setEditingTarget(null);
    setCreateSeed(draft);
    setEditorOpen(true);
  }, [location.key]);
  const [inspectTarget, setInspectTarget] = React.useState<{
    summary: SkillSummary;
    version: number | null;
  } | null>(null);
  // Track which skill rows have an in-flight subscription mutation so the
  // toggle / button on that row spins individually instead of disabling the
  // whole tab.
  const [pendingSubscriptionIds, setPendingSubscriptionIds] = React.useState<
    Set<string>
  >(() => new Set());
  const [pendingAiVersionIds, setPendingAiVersionIds] = React.useState<
    Set<string>
  >(() => new Set());
  // Edit-skill-metadata is a separate dialog from the version editor — name,
  // accessibility, and tags belong to the skill row, not to any single
  // version. Splitting them keeps users from wondering whether a tag they
  // changed will land on "this version" or "the whole skill."
  const [metadataEditTarget, setMetadataEditTarget] =
    React.useState<SkillSummary | null>(null);
  // Tags-only modal opened from the inline double-click affordance on the
  // Tags column. Separate from the metadata editor so the user gets exactly
  // the field they clicked into — nothing else.
  const [tagsEditTarget, setTagsEditTarget] =
    React.useState<SkillSummary | null>(null);
  // Track which skill_id (if any) is currently being deleted so we only
  // disable that row's button. Sharing a single mutation across all rows
  // would otherwise disable every row whenever any one delete is in flight.
  const [pendingDeleteSkillId, setPendingDeleteSkillId] = React.useState<
    string | null
  >(null);
  const [pendingDeleteSkill, setPendingDeleteSkill] =
    React.useState<SkillSummary | null>(null);
  const [pendingDeleteVersion, setPendingDeleteVersion] =
    React.useState<VersionDeleteTarget | null>(null);
  // Tracks rows whose visibility toggle is in flight. Set (not single-string)
  // because there's no dialog gating clicks across rows — two concurrent
  // toggles on different chips should each show their own spinner.
  const [pendingVisibilityIds, setPendingVisibilityIds] = React.useState<
    Set<string>
  >(() => new Set());

  const view = useResourceView<SkillSummary>({
    getRowId: (row) => row.skill.skill_id,
    initialSort: { columnId: "name", direction: "asc" },
    initialActiveFilters,
  });

  function openCreate() {
    setEditingTarget(null);
    // Manual create starts from a blank form — drop any chat-tool draft seed.
    setCreateSeed(null);
    setEditorOpen(true);
  }

  const openEdit = React.useCallback(
    (summary: SkillSummary, version: number | null) => {
      setEditingTarget({ summary, version });
      setEditorOpen(true);
    },
    [],
  );

  async function performSkillDelete(): Promise<void> {
    if (pendingDeleteSkill === null) {
      return;
    }
    setPendingDeleteSkillId(pendingDeleteSkill.skill.skill_id);
    try {
      await deleteSkill.mutateAsync(pendingDeleteSkill.skill.skill_id);
    } finally {
      setPendingDeleteSkillId(null);
      setPendingDeleteSkill(null);
    }
  }

  const setPending = React.useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<Set<string>>>,
      skillId: string,
      pending: boolean,
    ) => {
      setter((prev) => {
        const next = new Set(prev);
        if (pending) {
          next.add(skillId);
        } else {
          next.delete(skillId);
        }
        return next;
      });
    },
    [],
  );

  const handleSubscribe = React.useCallback(
    async (summary: SkillSummary) => {
      const skillId = summary.skill.skill_id;
      setPending(setPendingSubscriptionIds, skillId, true);
      try {
        await subscribeSkill.mutateAsync({ skillId });
      } finally {
        setPending(setPendingSubscriptionIds, skillId, false);
      }
    },
    [setPending, subscribeSkill],
  );

  const handleUnsubscribe = React.useCallback(
    async (summary: SkillSummary) => {
      const skillId = summary.skill.skill_id;
      setPending(setPendingSubscriptionIds, skillId, true);
      try {
        await unsubscribeSkill.mutateAsync({ skillId });
      } finally {
        setPending(setPendingSubscriptionIds, skillId, false);
      }
    },
    [setPending, unsubscribeSkill],
  );

  const handleSetAiVersion = React.useCallback(
    async (skillId: string, version: number | null) => {
      setPending(setPendingAiVersionIds, skillId, true);
      try {
        await setSubscription.mutateAsync({
          skillId,
          request: { ai_version: version },
        });
      } finally {
        setPending(setPendingAiVersionIds, skillId, false);
      }
    },
    [setPending, setSubscription],
  );

  const changeVisibility = React.useCallback(
    async (summary: SkillSummary, next: SkillAccessibility) => {
      if (summary.skill.created_by !== userId) {
        return;
      }
      // Selecting the tier the skill is already on is a no-op.
      if (next === summary.skill.accessibility) {
        return;
      }
      const skillId = summary.skill.skill_id;
      setPendingVisibilityIds((prev) => {
        const updated = new Set(prev);
        updated.add(skillId);
        return updated;
      });
      try {
        await updateMetadata.mutateAsync({
          skillId,
          request: { accessibility: next },
        });
      } finally {
        setPendingVisibilityIds((prev) => {
          if (!prev.has(skillId)) {
            return prev;
          }
          const updated = new Set(prev);
          updated.delete(skillId);
          return updated;
        });
      }
    },
    [updateMetadata, userId],
  );

  const allSkills = React.useMemo(
    () => skillsQuery.data?.items ?? [],
    [skillsQuery.data],
  );

  // Skills filter in memory rather than server-side, so we work with the
  // structured `Filter[]` directly; `"none"` and `"roboql"` (never
  // produced here in v1) collapse to an empty list with the seed match mode.
  // Memoize the narrowed slice so dependent hooks stay stable when filter
  // state hasn't moved — the ternary creates a fresh `[]` per render otherwise.
  const structuredFilters = React.useMemo<Filter[]>(
    () => (view.filters.type === "structured" ? view.filters.filters : []),
    [view.filters],
  );
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";
  const { resetCurrentPage } = view.table;

  // Field definitions for the shared DataTable filter widget. The tag field
  // is a ``set`` (CONTAINS / NOT_CONTAINS membership) whose options are the
  // distinct tags actually in use; that option list is recomputed per tab
  // because Personal and Org load different skill slices. Name / Author are
  // restricted to the two comparators that carry meaning for these free-text
  // columns.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(() => {
    const allTags = new Set<string>();
    for (const summary of allSkills) {
      for (const tag of summary.skill.tags) {
        allTags.add(tag);
      }
    }
    return [
      {
        type: "string",
        field: "name",
        name: "Name",
        allowedComparators: ["CONTAINS", "EQUALS"],
      },
      {
        type: "string",
        field: "created_by",
        name: "Author",
        allowedComparators: ["CONTAINS", "EQUALS"],
      },
      {
        type: "set",
        field: "tags",
        name: "Tag",
        options: [...allTags].sort((a, b) => a.localeCompare(b)),
      },
    ];
  }, [allSkills]);

  // Clicking a tag chip in a row appends an exact-match tag filter — the
  // same affordance the handwritten chip strip used to provide, now routed
  // through the DataTable filter state. Deduped so a repeated click on an
  // already-applied tag is a no-op rather than stacking identical chips.
  const addTagFilter = React.useCallback(
    (tag: string) => {
      const alreadyApplied = structuredFilters.some(
        (f) =>
          f.field === "tags" &&
          f.comparator === "CONTAINS" &&
          f.values.length === 1 &&
          f.values[0] === tag,
      );
      if (alreadyApplied) {
        return;
      }
      view.commit({
        type: "structured",
        filter: {
          type: "set",
          field: "tags",
          comparator: "CONTAINS",
          values: [tag],
        },
      });
    },
    [structuredFilters, view],
  );

  // Skills are loaded in full, so the DataTable filter widget's `Filter[]`
  // is evaluated in memory rather than sent server-side as a condition.
  const filteredSkills = React.useMemo(() => {
    if (structuredFilters.length === 0) {
      return allSkills;
    }
    return allSkills.filter((s) =>
      skillMatchesFilters(s, structuredFilters, matchMode),
    );
  }, [allSkills, structuredFilters, matchMode]);

  // The DataTable primitive is wired for server-side sort (its DEFAULT_COL_DEF
  // sets `comparator: () => 0` to inhibit AG Grid's client-side reorder so the
  // server stays authoritative). Skills load in one shot, so we apply the sort
  // ourselves before slicing — column headers still surface the affordance and
  // direction indicator via `sortable: true` on the columns below.
  const sortedSkills = React.useMemo(() => {
    if (view.table.sort === null) {
      return filteredSkills;
    }
    const { columnId, direction } = view.table.sort;
    const direction_factor = direction === "asc" ? 1 : -1;
    const key = (s: SkillSummary): string => {
      switch (columnId) {
        case "name":
          return s.skill.name;
        case "created_by":
          return s.skill.created_by;
        default:
          return "";
      }
    };
    return [...filteredSkills].sort(
      (a, b) =>
        key(a).localeCompare(key(b), undefined, { sensitivity: "base" }) *
        direction_factor,
    );
  }, [filteredSkills, view.table.sort]);

  // Client-side pagination: the skills list is fetched in one shot, but the
  // DataTable footer drives its own page state. Slice locally so prev/next
  // and the "1-N of M" range label behave.
  const start = view.table.currentPage * view.table.pageSize;
  const end = start + view.table.pageSize;
  const visibleSkills = React.useMemo(
    () => sortedSkills.slice(start, end),
    [sortedSkills, start, end],
  );
  const hasNextPage = end < sortedSkills.length;

  const columns = React.useMemo<ColDef<SkillSummary>[]>(() => {
    const baseColumns: ColDef<SkillSummary>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        valueGetter: (p) => p.data?.skill.name ?? "",
      },
      {
        colId: "created_by",
        headerName: "Author",
        flex: 1,
        sortable: true,
        valueGetter: (p) => p.data?.skill.created_by ?? "",
      },
      {
        colId: "visibility",
        headerName: "Visibility",
        width: 160,
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const accessibility = summary.skill.accessibility;
          const isAuthor = summary.skill.created_by === userId;
          const isPending = pendingVisibilityIds.has(summary.skill.skill_id);
          // Non-authors can't change visibility — show a read-only chip.
          if (!isAuthor) {
            return (
              <RobotoTooltip title="Only the author can change visibility">
                <span>
                  <VisibilityChip accessibility={accessibility} />
                </span>
              </RobotoTooltip>
            );
          }
          // Author: a dropdown that jumps straight to any tier. Unlike a
          // click-to-cycle chip, this never forces a downgrade through
          // `private` (which would prune every non-author subscription).
          return (
            <VisibilitySelectCell
              summary={summary}
              accessibility={accessibility}
              isPending={isPending}
              onChange={(s, next) => void changeVisibility(s, next)}
            />
          );
        },
      },
      {
        colId: "tags",
        headerName: "Tags",
        flex: 2,
        sortable: false,
        // AG Grid fires dblclick at the cell level regardless of inner DOM —
        // way more reliable than putting an onDoubleClick on a wrapper div
        // that gets sized to its content (a chip-tall band, or zero on empty
        // cells, both of which we've broken before). Chip-originated dblclicks
        // are filtered out here so a fast double-tap on a tag chip stays a
        // filter action and never escalates into the editor.
        onCellDoubleClicked: (event) => {
          const summary = event.data;
          if (!summary || !canEditSkill(summary, userId)) {
            return;
          }
          const target = (event.event?.target ?? null) as HTMLElement | null;
          if (target?.closest(".MuiChip-root")) {
            return;
          }
          setTagsEditTarget(summary);
        },
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const tags = summary.skill.tags;
          const canEdit = canEditSkill(summary, userId);
          let cellTitle: string | undefined;
          if (canEdit && tags.length > 0) {
            cellTitle = "Click a tag to filter; double-click to edit";
          } else if (canEdit) {
            cellTitle = "Double-click to add tags";
          }
          return (
            <div className={styles.tagsCell} title={cellTitle}>
              <SkillTagsCell tags={tags} onTagClick={addTagFilter} />
            </div>
          );
        },
      },
      {
        colId: "latest",
        headerName: "Latest version",
        width: 140,
        valueGetter: (p) =>
          p.data?.latest_version
            ? `v${p.data.latest_version.version}`
            : "Empty",
      },
      // The AI-version chip only ever applies to rows the caller has subscribed
      // to. On the Org tab every row is unsubscribed-by-definition, so the
      // chip would always be empty there — hide the whole column on that tab.
      ...(tab === "org"
        ? []
        : [
            {
              colId: "ai_indicator",
              headerName: "AI",
              width: 90,
              cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
                if (!p.data) {
                  return null;
                }
                const pinned = p.data.subscription?.ai_version;
                if (pinned === null || pinned === undefined) {
                  return null;
                }
                const latest = p.data.latest_version?.version;
                const isStale = latest !== undefined && latest !== pinned;
                return (
                  <RobotoTooltip
                    title={
                      isStale
                        ? `AI invokes v${pinned} (latest is v${latest}). Switch version in the expanded panel.`
                        : `AI invokes v${pinned} (latest).`
                    }
                  >
                    <Chip
                      size="small"
                      label={`v${pinned}`}
                      variant="outlined"
                      color={isStale ? "warning" : "default"}
                      className={styles.aiPinnedChip}
                    />
                  </RobotoTooltip>
                );
              },
            } satisfies ColDef<SkillSummary>,
          ]),
      {
        colId: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<SkillSummary>) => {
          if (!p.data) {
            return null;
          }
          const summary = p.data;
          const isAuthor = summary.skill.created_by === userId;
          const subscribed = summary.subscription !== null;
          const subscriptionPending = pendingSubscriptionIds.has(
            summary.skill.skill_id,
          );
          if (!isAuthor && !subscribed) {
            // Org tab: subscribe, no edit / delete affordances. Inspect lives
            // on each version row inside the expanded panel.
            return (
              <div className={styles.actions}>
                <RobotoTooltip title="Subscribe — moves to your Personal tab">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => void handleSubscribe(summary)}
                      disabled={subscriptionPending}
                      loading={subscriptionPending}
                    >
                      Subscribe
                    </Button>
                  </span>
                </RobotoTooltip>
              </div>
            );
          }
          if (!isAuthor) {
            // Personal tab, subscribed-but-not-authored. On an org-editable
            // skill the subscriber may also edit name / tags; everyone else
            // just gets Unsubscribe. Inspect is on each version row.
            const canEdit = canEditSkill(summary, userId);
            return (
              <div className={styles.actions}>
                {canEdit && (
                  <RobotoTooltip title="Edit name and tags">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setMetadataEditTarget(summary)}
                        aria-label="Edit skill"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                )}
                <RobotoTooltip title="Unsubscribe — removes from your Personal tab">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleUnsubscribe(summary)}
                      disabled={subscriptionPending}
                      aria-label="Unsubscribe from skill"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </RobotoTooltip>
              </div>
            );
          }
          // Author: full edit + delete.
          const isDeleting = pendingDeleteSkillId === summary.skill.skill_id;
          return (
            <div className={styles.actions}>
              <RobotoTooltip title="Edit name, accessibility, and tags">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setMetadataEditTarget(summary)}
                    aria-label="Edit skill"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </RobotoTooltip>
              <RobotoTooltip title="Delete the entire skill (permanent)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setPendingDeleteSkill(summary)}
                    disabled={isDeleting}
                    aria-label="Delete skill"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </RobotoTooltip>
            </div>
          );
        },
      },
    ];
    // DataTable's DEFAULT_COL_DEF hides columns by default (the
    // column-visibility picker is the source of truth for opt-in consumers).
    // Skills doesn't use that system — every column is meant to be visible —
    // so flip the default. Spread order lets any column still opt out by
    // setting `hide: true` explicitly.
    return baseColumns.map((c) => ({ hide: false, ...c }));
  }, [
    addTagFilter,
    handleSubscribe,
    handleUnsubscribe,
    changeVisibility,
    pendingDeleteSkillId,
    pendingSubscriptionIds,
    pendingVisibilityIds,
    tab,
    userId,
  ]);

  let loading: DataTableLoadingState = "idle";
  if (orgId === undefined || skillsQuery.isLoading) {
    loading = "foreground";
  } else if (skillsQuery.isFetching) {
    loading = "background";
  }

  const renderVersionsPanel = React.useCallback(
    (summary: SkillSummary) => (
      <div className={styles.expandedContainer}>
        <VersionsSubTable
          summary={summary}
          isAuthor={summary.skill.created_by === userId}
          canEdit={canEditSkill(summary, userId)}
          subscribed={summary.subscription !== null}
          aiPinnedVersion={summary.subscription?.ai_version ?? null}
          aiVersionPending={pendingAiVersionIds.has(summary.skill.skill_id)}
          onSetAiVersion={(version) =>
            void handleSetAiVersion(summary.skill.skill_id, version)
          }
          onInspectVersion={(version) => setInspectTarget({ summary, version })}
          onEditVersion={(version) => openEdit(summary, version)}
          onRequestDeleteVersion={(version, isLast) =>
            setPendingDeleteVersion({
              summary,
              version,
              isLastVersion: isLast,
            })
          }
        />
      </div>
    ),
    [handleSetAiVersion, openEdit, pendingAiVersionIds, userId],
  );

  return (
    <div className={styles.page}>
      {skillsQuery.isError && (
        <Alert severity="error" className={styles.errorAlert}>
          Failed to load skills.
        </Alert>
      )}

      <div className={styles.toolbar}>
        <Tabs
          value={tab}
          onChange={(_, next: SkillListScope) => {
            setTab(next);
            resetCurrentPage();
          }}
        >
          <Tab value="personal" label="Personal" />
          <Tab value="org" label="Org" />
        </Tabs>
        <Button variant="contained" onClick={openCreate}>
          Create Skill
        </Button>
      </div>

      <div className={styles.tableSlot}>
        <ResourceView.Root>
          <ResourceView.Actions>
            <ResourceView.Filters
              filterDefinitions={filterDefinitions}
              onFilterSelected={view.commit}
            />
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={filterDefinitions}
            onRemove={view.removeFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<SkillSummary>
            table={view.table}
            hasNextPage={hasNextPage}
            loading={loading}
            columns={columns}
            data={visibleSkills}
            totalRowCount={filteredSkills.length}
            noRowsContent={
              allSkills.length === 0 ? (
                <span>
                  No skills yet. Click <strong>Create Skill</strong> to get
                  started.
                </span>
              ) : (
                <span>No skills match the current filters.</span>
              )
            }
            expansion={{
              renderRow: renderVersionsPanel,
              initialExpandedIds,
            }}
          />
        </ResourceView.Root>
      </div>

      <SkillEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        summary={editingTarget?.summary ?? null}
        editingVersion={editingTarget?.version ?? null}
        initialCreateValues={createSeed}
        orgId={orgId}
      />

      <SkillMetadataEditor
        open={metadataEditTarget !== null}
        onClose={() => setMetadataEditTarget(null)}
        summary={metadataEditTarget}
        orgId={orgId}
        userId={userId}
      />

      <TagsEditor
        open={tagsEditTarget !== null}
        onClose={() => setTagsEditTarget(null)}
        summary={tagsEditTarget}
        orgId={orgId}
      />

      <SkillInspector
        open={inspectTarget !== null}
        onClose={() => setInspectTarget(null)}
        summary={inspectTarget?.summary ?? null}
        initialVersion={inspectTarget?.version ?? null}
      />

      <AlertDialog
        dialogOpen={pendingDeleteSkill !== null}
        handleClose={() => {
          if (pendingDeleteSkillId === null) {
            setPendingDeleteSkill(null);
          }
        }}
        dialogTitle={
          pendingDeleteSkill
            ? `Delete skill "${pendingDeleteSkill.skill.name}"?`
            : "Delete skill"
        }
        dialogContent={
          <div className={styles.deleteWarning}>
            <span className={styles.deleteWarningEmphasis}>
              This is permanent.
            </span>
            <span>
              The skill and all of its versions will be removed. Existing chats
              keep any skill_invocation messages they&apos;ve already produced —
              the body is captured at invocation time. You can re-create a skill
              with the same name afterwards.
            </span>
          </div>
        }
        dialogAction={performSkillDelete}
      />

      <DeleteVersionDialog
        target={pendingDeleteVersion}
        orgId={orgId}
        onClose={() => setPendingDeleteVersion(null)}
      />
    </div>
  );
}

const MAX_VISIBLE_TAGS = 2;

interface SkillTagsCellProps {
  tags: string[];
  /** Optional click handler — when set, each chip becomes a filter trigger. */
  onTagClick?: (tag: string) => void;
}

function SkillTagsCell({ tags, onTagClick }: SkillTagsCellProps) {
  const sorted = [...tags].sort();
  const visible = sorted.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = sorted.length - visible.length;
  return (
    <>
      {visible.map((tag) => {
        // Same deterministic color hash as the shared <Tag> renders elsewhere
        // — keyed off the tag string, with a small "error/warn/success" bias.
        // 35% alpha for the rest state, 55% on hover, mirroring shared/Tags.
        const rgb = assignTagColor(tag).join(", ");
        return (
          <Chip
            key={tag}
            size="small"
            label={tag}
            onClick={
              onTagClick
                ? (e) => {
                    // Stop the dblclick from bubbling to the cell — clicking
                    // a chip should only ever add a filter, never open the
                    // editor.
                    e.stopPropagation();
                    onTagClick(tag);
                  }
                : undefined
            }
            clickable={onTagClick !== undefined}
            sx={{
              backgroundColor: `rgba(${rgb}, 0.35)`,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: `rgba(${rgb}, 0.55)`,
              },
            }}
          />
        );
      })}
      {overflowCount > 0 && (
        <span
          className={styles.tagOverflow}
          title={sorted.slice(MAX_VISIBLE_TAGS).join(", ")}
        >
          +{overflowCount} more
        </span>
      )}
    </>
  );
}

interface VersionsSubTableProps {
  summary: SkillSummary;
  /** True when the caller authored the skill. Author-only: deleting the last
   * remaining version (which cascades into a whole-skill delete). */
  isAuthor: boolean;
  /** True when the caller may edit version content — the author, or a
   * subscriber of an org-editable skill. Drives Edit/Delete vs. Inspect. */
  canEdit: boolean;
  /** True when the caller has a subscription row (always true for authors). */
  subscribed: boolean;
  /** Currently pinned version for the caller's AI, or null if not exposed to AI. */
  aiPinnedVersion: number | null;
  /** True while a setSubscription mutation is in flight for this skill. */
  aiVersionPending: boolean;
  onSetAiVersion: (version: number | null) => void;
  onInspectVersion: (version: number) => void;
  onEditVersion: (version: number) => void;
  onRequestDeleteVersion: (version: number, isLast: boolean) => void;
}

/**
 * Per-version table rendered inside an expanded skill row. Each row shows:
 *
 * - the version number,
 * - the "when to use" text,
 * - an **Available to AI** Switch wired to the caller's subscription
 *   ``ai_version`` (at most one version per skill can be on — flipping a
 *   second version takes the pin from the previously-on row),
 * - and either Edit / Delete (author or org-editable subscriber) or Inspect
 *   (everyone else) actions.
 */
function VersionsSubTable({
  summary,
  isAuthor,
  canEdit,
  subscribed,
  aiPinnedVersion,
  aiVersionPending,
  onSetAiVersion,
  onInspectVersion,
  onEditVersion,
  onRequestDeleteVersion,
}: VersionsSubTableProps) {
  const versionsQuery = useSkillVersions(summary.skill.skill_id);

  const versions = versionsQuery.data ?? [];

  if (versionsQuery.isLoading) {
    return <div className={styles.versionEmpty}>Loading versions…</div>;
  }
  if (versions.length === 0) {
    return <div className={styles.versionEmpty}>No versions yet.</div>;
  }

  return (
    <Table size="small" className={styles.versionsTable}>
      <TableHead>
        <TableRow>
          <TableCell width={80}>Version</TableCell>
          <TableCell>When to use</TableCell>
          {subscribed && (
            <TableCell width={120} align="center">
              Available to AI
            </TableCell>
          )}
          <TableCell width={120} align="right">
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {versions.map((version) => {
          const isLastVersion = versions.length === 1;
          const isPinned = aiPinnedVersion === version.version;
          let aiTooltip: string;
          if (isPinned) {
            aiTooltip = `AI invokes this version. Click to disable.`;
          } else if (aiPinnedVersion !== null) {
            aiTooltip = `Click to switch the AI pin from v${aiPinnedVersion} to v${version.version}.`;
          } else {
            aiTooltip = `Click to pin v${version.version} for AI auto-invocation.`;
          }
          // A non-author editor may delete versions, but not the last one —
          // that cascades into a whole-skill delete, which is author-only.
          const lastVersionDeleteBlocked = !isAuthor && isLastVersion;
          let deleteTooltip: string;
          if (lastVersionDeleteBlocked) {
            deleteTooltip =
              "Only the author can delete the skill's last remaining version.";
          } else if (isLastVersion) {
            deleteTooltip =
              "Deleting the only version will also delete the skill";
          } else {
            deleteTooltip = `Delete v${version.version}`;
          }
          return (
            <TableRow key={version.version}>
              <TableCell>v{version.version}</TableCell>
              <TableCell>
                <div
                  className={styles.versionDescription}
                  title={version.description}
                >
                  {version.description}
                </div>
              </TableCell>
              {subscribed && (
                <TableCell align="center">
                  <RobotoTooltip title={aiTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        color={isPinned ? "primary" : "default"}
                        onClick={() =>
                          onSetAiVersion(isPinned ? null : version.version)
                        }
                        disabled={aiVersionPending}
                        aria-label={`Available to AI for ${summary.skill.name} v${version.version}`}
                        aria-pressed={isPinned}
                      >
                        {isPinned ? (
                          <PushPinIcon fontSize="small" />
                        ) : (
                          <PushPinOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                </TableCell>
              )}
              <TableCell align="right">
                <div className={styles.actions}>
                  {canEdit ? (
                    <>
                      {/* Authors edit in a raw textarea; an Inspect affordance
                          lets them preview the rendered Markdown body too. */}
                      <RobotoTooltip title={`Inspect v${version.version}`}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onInspectVersion(version.version)}
                            aria-label={`Inspect v${version.version}`}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                      <RobotoTooltip title={`Edit v${version.version}`}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onEditVersion(version.version)}
                            aria-label={`Edit v${version.version}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                      <RobotoTooltip title={deleteTooltip}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onRequestDeleteVersion(
                                version.version,
                                isLastVersion,
                              )
                            }
                            disabled={lastVersionDeleteBlocked}
                            aria-label={`Delete v${version.version}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </RobotoTooltip>
                    </>
                  ) : (
                    <RobotoTooltip title={`Inspect v${version.version}`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onInspectVersion(version.version)}
                          aria-label={`Inspect v${version.version}`}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </RobotoTooltip>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface DeleteVersionDialogProps {
  target: VersionDeleteTarget | null;
  orgId: string | undefined;
  onClose: () => void;
}

function DeleteVersionDialog({
  target,
  orgId,
  onClose,
}: DeleteVersionDialogProps) {
  const deleteVersion = useDeleteVersion(orgId);

  async function performDelete(): Promise<void> {
    if (target === null) {
      return;
    }
    try {
      await deleteVersion.mutateAsync({
        skillId: target.summary.skill.skill_id,
        version: target.version,
      });
    } finally {
      onClose();
    }
  }

  const title = target
    ? `Delete version v${target.version} of "${target.summary.skill.name}"?`
    : "Delete version";

  return (
    <AlertDialog
      dialogOpen={target !== null}
      handleClose={() => {
        if (!deleteVersion.isPending) {
          onClose();
        }
      }}
      dialogTitle={title}
      dialogContent={
        <div className={styles.deleteWarning}>
          <span className={styles.deleteWarningEmphasis}>
            This is permanent.
          </span>
          <span>
            {target?.isLastVersion
              ? "This is the only remaining version. Deleting it will also remove the skill itself."
              : "Existing chats keep any skill_invocation messages they've already produced — the body is captured at invocation time."}
          </span>
        </div>
      }
      dialogAction={performDelete}
    />
  );
}
